import React, { useEffect, useMemo, useState, ReactNode } from 'react';
import { incidentService, Incident, CreateIncidentData } from '../../api/incidents';
import { tripService, Trip } from '../../api/trips';
import { vehicleService, Vehicle } from '../../api/vehicles';
import { employeeService, Employee } from '../../api/Employee';
import {
  AlertTriangle,
  Search,
  Plus,
  Car,
  MapPin,
  Calendar,
  Clock,
  Image as ImageIcon,
  Edit2,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Upload,
  X,
  Users,
  Info,
  FileText,
} from 'lucide-react';
import {
  PageHeader,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Th,
  Tr,
  Td,
  Input,
  Button,
  ConfirmModal,
  Modal,
  Label,
  Select,
  Textarea,
} from '../../components/ui/SharedLayout';

const getApiErrorMessage = (err: unknown, fallback: string) => {
  if (
    typeof err === 'object' &&
    err !== null &&
    'response' in err &&
    typeof err.response === 'object' &&
    err.response !== null &&
    'data' in err.response
  ) {
    const responseData = err.response.data as { message?: string | string[] };
    if (Array.isArray(responseData.message)) {
      return responseData.message.join(', ');
    }
    if (responseData.message) {
      return responseData.message;
    }
  }

  if (err instanceof Error && err.message) {
    return err.message;
  }

  return fallback;
};

const ITEMS_PER_PAGE = 20;

const MAX_DESCRICAO = 500;
const MAX_LOCAL = 200;

type ToastType = 'success' | 'error';
interface ToastState {
  type: ToastType;
  message: string;
}

type FieldWrapperProps = {
  children: ReactNode;
  error?: string;
  label?: ReactNode;
  htmlFor?: string;
  required?: boolean;
  hint?: string;
};

const FieldWrapper = ({ children, error, label, htmlFor, required, hint }: FieldWrapperProps) => (
  <div className="flex flex-col gap-1">
    {label ? (
      <Label htmlFor={htmlFor} className="mb-0.5">
        <span className="inline-flex items-center gap-0.5">
          {label}
          {required ? <span className="text-rose-500 ml-0.5 font-bold leading-none">*</span> : null}
        </span>
      </Label>
    ) : null}
    {children}
    {hint && !error ? (
      <p className="mt-0.5 text-[11px] font-medium leading-tight text-slate-500">{hint}</p>
    ) : null}
    {error ? (
      <p className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-semibold leading-tight text-rose-600">
        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
        {error}
      </p>
    ) : null}
  </div>
);

const CharCounter = ({ value, max }: { value: string; max: number }) => (
  <span className={`shrink-0 text-[11px] font-semibold tabular-nums ${value.length > max ? 'text-rose-600' : 'text-slate-500'}`}>
    {value.length}/{max}
  </span>
);

const IncidentesPage = () => {
  const [incidentes, setIncidentes] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIncidente, setEditingIncidente] = useState<Incident | null>(null);
  const [_trips, setTrips] = useState<Trip[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [formData, setFormData] = useState<Partial<CreateIncidentData> & { involved_employees?: number[] }>({
    vehicle_id: undefined,
    date: '',
    time: '',
    description: '',
    location: '',
    involved_employees: [],
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deletingIncidente, setDeletingIncidente] = useState<Incident | null>(null);

  const showToast = (type: ToastType, message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const closeFormModal = () => {
    setIsModalOpen(false);
    setEditingIncidente(null);
    setFormData({ vehicle_id: undefined, date: '', time: '', description: '', location: '', involved_employees: [] });
    setPhotoFiles([]);
    setPhotoPreviews([]);
    setFormErrors({});
  };

  const vehicleMap = useMemo(() => {
    const map = new Map<number, Vehicle>();
    vehicles.forEach(v => map.set(v.id, v));
    return map;
  }, [vehicles]);

  useEffect(() => {
    fetchIncidentes();
    tripService.getAll().then(trips => setTrips(trips));
    vehicleService.getAllVehicles().then(vehicles => setVehicles(vehicles));
    employeeService.getAllEmployees().then(employees => setEmployees(employees));
  }, []);

  const fetchIncidentes = async () => {
    setLoading(true);
    setError(null);
    try {
      const incidentes = await incidentService.getAll();
      setIncidentes(incidentes);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao carregar incidentes'));
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field: keyof typeof formData, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setFormErrors(prev => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field as string];
      return next;
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'select-multiple') {
      const select = e.target as HTMLSelectElement;
      const selectedOptions = Array.from(select.selectedOptions).map(option => Number(option.value));
      handleFormChange(name as keyof typeof formData, selectedOptions);
    } else {
      handleFormChange(name as keyof typeof formData, value);
    }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setPhotoFiles(prev => [...prev, ...files]);
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setPhotoPreviews(prev => [...prev, ...newPreviews]);
  };

  const handleRemovePhoto = (idx: number) => {
    setPhotoFiles(prev => prev.filter((_, i) => i !== idx));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const handleCreate = () => {
    setEditingIncidente(null);
    setFormData({ vehicle_id: undefined, date: '', time: '', description: '', location: '', involved_employees: [] });
    setPhotoFiles([]);
    setPhotoPreviews([]);
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleEdit = (incidente: Incident) => {
    setEditingIncidente(incidente);
    setFormData({
      vehicle_id: incidente.vehicle_id,
      date: incidente.date || '',
      time: incidente.time || '',
      description: incidente.description || '',
      location: incidente.location || '',
      involved_employees: incidente.involved_employees || [],
    });
    setPhotoFiles([]);
    setPhotoPreviews([]);
    setFormErrors({});
    setIsModalOpen(true);
  };

  const abrirConfirmacaoExclusao = (incidente: Incident) => {
    setDeletingIncidente(incidente);
    setIsDeleteConfirmOpen(true);
  };

  const confirmarExclusao = async () => {
    if (!deletingIncidente) return;
    setSubmitting(true);
    try {
      await incidentService.delete(deletingIncidente.id);
      showToast('success', 'Incidente excluído com sucesso!');
      await fetchIncidentes();
    } catch (err: unknown) {
      showToast('error', getApiErrorMessage(err, 'Erro ao excluir incidente'));
    } finally {
      setSubmitting(false);
      setIsDeleteConfirmOpen(false);
      setDeletingIncidente(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (!formData.vehicle_id || Number.isNaN(Number(formData.vehicle_id))) nextErrors.vehicle_id = 'Selecione um veículo';
    if (!formData.date) nextErrors.date = 'Informe a data do incidente';
    if (!formData.time) nextErrors.time = 'Informe a hora do incidente';
    if (!formData.description?.trim()) nextErrors.description = 'Informe a descrição do ocorrido';
    else if (formData.description.length > MAX_DESCRICAO) nextErrors.description = `Máximo de ${MAX_DESCRICAO} caracteres`;
    if (!formData.location?.trim()) nextErrors.location = 'Informe o local do incidente';
    else if (formData.location.length > MAX_LOCAL) nextErrors.location = `Máximo de ${MAX_LOCAL} caracteres`;
    if (!formData.involved_employees || formData.involved_employees.length === 0) nextErrors.involved_employees = 'Selecione pelo menos um envolvido';
    setFormErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      showToast('error', 'Verifique os campos destacados antes de continuar.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const imagesBase64 = await Promise.all(photoFiles.map(file => {
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }));
      const payload: CreateIncidentData = {
        vehicle_id: Number(formData.vehicle_id),
        date: formData.date || '',
        time: formData.time || '',
        description: formData.description || '',
        location: formData.location || '',
        involved_employees: formData.involved_employees || [],
        images: imagesBase64,
      };
      if (editingIncidente) {
        await incidentService.update(editingIncidente.id, payload);
        showToast('success', 'Incidente atualizado com sucesso!');
      } else {
        await incidentService.create(payload);
        showToast('success', 'Incidente registrado com sucesso!');
      }
      closeFormModal();
      await fetchIncidentes();
    } catch (err: unknown) {
      const msg = getApiErrorMessage(err, 'Erro ao salvar incidente');
      setError(msg);
      showToast('error', msg);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredIncidentes = useMemo(() => {
    return incidentes
      .filter(i =>
        (i.description?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (i.location?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        i.id.toString().includes(searchTerm)
      )
      .sort((a, b) => b.id - a.id);
  }, [incidentes, searchTerm]);

  const totalPaginas = useMemo(
    () => Math.max(1, Math.ceil(filteredIncidentes.length / ITEMS_PER_PAGE)),
    [filteredIncidentes.length],
  );

  useEffect(() => {
    if (currentPage > totalPaginas) setCurrentPage(1);
  }, [currentPage, totalPaginas]);

  const { incidentesPaginados, pageRange } = useMemo(() => {
    const total = filteredIncidentes.length;
    const safePage = Math.min(currentPage, totalPaginas);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return {
      incidentesPaginados: filteredIncidentes.slice(startIndex, endIndex),
      pageRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [filteredIncidentes, currentPage, totalPaginas]);

  const getPageNumbers = useMemo(() => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPaginas <= maxVisible + 2) {
      for (let i = 1; i <= totalPaginas; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPaginas - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPaginas - 1) pages.push('...');
    pages.push(totalPaginas);
    return pages;
  }, [currentPage, totalPaginas]);

  const handleImageClick = (url: string) => setSelectedImage(url);
  const handleCloseImageModal = () => setSelectedImage(null);

  const renderInvolvedEmployees = (ids: number[] = []) => (
    <div className="flex flex-col gap-1">
      {ids.map(id => {
        const emp = employees.find(e => e.id === id);
        return emp ? (
          <div key={id} className="text-sm text-gray-900">
            <span className="font-semibold">{emp.name}</span>
            <div className="text-xs text-gray-500">{emp.cpf}</div>
          </div>
        ) : null;
      })}
    </div>
  );

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr?: string): { date: string; time: string } => {
    if (!dateStr) return { date: '-', time: '-' };
    try {
      const d = new Date(dateStr);
      return {
        date: d.toLocaleDateString('pt-BR'),
        time: d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
      };
    } catch {
      return { date: dateStr || '-', time: '-' };
    }
  };

  return (
    <>
      <PageHeader title="Incidentes" hideTitleInBody />

      {toast && (
        <div
          className={`fixed top-5 right-5 z-[100] inline-flex items-center gap-2.5 rounded-xl px-4 py-3 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.35)] border animate-[fadeIn_0.2s_ease-out] ${
            toast.type === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-rose-50 border-rose-200 text-rose-800'
          }`}
        >
          {toast.type === 'success' ? (
            <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
          ) : (
            <XCircle className="h-5 w-5 text-rose-600 shrink-0" />
          )}
          <span className="text-[13px] font-semibold leading-tight">{toast.message}</span>
        </div>
      )}

      <div className="flex flex-col gap-5">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[15px] font-semibold leading-tight text-rose-600">
              Gestão de incidentes
            </p>
            <h1 className="mt-1 text-[22px] sm:text-[26px] font-bold leading-[1.15] tracking-tight text-slate-900">
              Registre, acompanhe e resolva os incidentes.
              <span className="ml-1.5 text-slate-500 font-semibold text-[18px] sm:text-[20px]">
                Mantenha o histórico completo de ocorrências da frota.
              </span>
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="primary" size="lg" onClick={handleCreate} disabled={submitting}>
              <Plus className="h-[18px] w-[18px] stroke-[2.1]" />
              Registrar
            </Button>
          </div>
        </div>

        {error && (
          <div className="inline-flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
            <AlertCircle className="h-5 w-5 shrink-0 mt-0.5 text-rose-600" />
            <div>
              <p className="text-[13px] font-semibold">Atenção</p>
              <p className="text-[13px] font-medium mt-0.5 text-rose-700/90">{error}</p>
            </div>
          </div>
        )}

        <SectionCard>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-rose-500 via-orange-500 to-amber-500 text-white shadow-[0_6px_16px_-8px_rgba(239,68,68,0.6)]">
                <AlertTriangle className="h-[18px] w-[18px] stroke-[1.9]" />
              </div>
              <div>
                <h2 className="text-[15px] font-bold tracking-tight text-slate-900">
                  Todos incidentes
                </h2>
                <p className="text-[12px] font-medium text-slate-500">
                  {pageRange.total > 0
                    ? `${pageRange.start} a ${pageRange.end} de ${pageRange.total} incidentes listados (total cadastrado: ${incidentes.length})`
                    : 'Nenhum incidente listado'}
                </p>
              </div>
            </div>
            <div className="relative w-full sm:!w-[320px] lg:!w-[360px]">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
                <Search className="h-[17px] w-[17px] stroke-[1.8]" />
              </div>
              <Input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Buscar descrição, local ou ID..."
                className="pl-10"
              />
            </div>
          </CardHeader>

          <CardBody>
            <ModernTable className="table-fixed layout-fixed">
              <Thead>
                <Tr>
                  <Th className="w-[17%] min-w-[220px] max-w-[280px]">Veículo</Th>
                  <Th className="w-[15%] min-w-[170px] max-w-[240px]">Local</Th>
                  <Th className="w-[9%] min-w-[110px] max-w-[140px]">Data</Th>
                  <Th className="w-[8%] min-w-[90px] max-w-[120px]">Hora</Th>
                  <Th className="w-[25%] min-w-[240px] max-w-[420px]">Descrição</Th>
                  <Th className="w-[11%] min-w-[120px] max-w-[170px]">Fotos</Th>
                  <Th className="w-[9%] min-w-[110px] max-w-[150px]">Criado em</Th>
                  <Th className="w-[6%] min-w-[80px] max-w-[100px]">Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-5 py-14 text-center text-sm font-medium text-slate-500"
                    >
                      <div className="inline-flex items-center justify-center gap-2">
                        <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                        Carregando incidentes...
                      </div>
                    </td>
                  </tr>
                ) : filteredIncidentes.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-5 py-14 text-center text-sm font-medium text-slate-500"
                    >
                      {searchTerm
                        ? 'Nenhum incidente corresponde à busca.'
                        : 'Nenhum incidente cadastrado.'}
                    </td>
                  </tr>
                ) : (
                  incidentesPaginados.map((incidente) => {
                    const vehicle = vehicleMap.get(incidente.vehicle_id);
                    const criado = formatDateTime(incidente.created_at);
                    const qtdFotos = (incidente.images && Array.isArray(incidente.images))
                      ? incidente.images.length
                      : 0;
                    return (
                      <Tr key={incidente.id}>
                        <Td className="max-w-[280px]">
                          <div className="min-w-0 w-full max-w-[280px] overflow-hidden">
                            <div className="flex items-center gap-3 min-w-0 w-full max-w-full">
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-slate-500 via-slate-600 to-slate-700 text-white shadow-[0_6px_16px_-8px_rgba(71,85,105,0.6)]">
                                <Car className="h-[18px] w-[18px] stroke-[1.9]" />
                              </div>
                              <div className="min-w-0 flex-1 max-w-full">
                                <div
                                  className="truncate font-semibold text-slate-900 w-full"
                                  title={vehicle?.brand || '-'}
                                >
                                  {vehicle?.brand || '-'}
                                </div>
                                <div
                                  className="mt-0.5 text-[12px] text-slate-500 truncate w-full font-mono"
                                  title={vehicle?.license_plate || '-'}
                                >
                                  Placa {vehicle?.license_plate || '-'}
                                </div>
                              </div>
                            </div>
                          </div>
                        </Td>
                        <Td className="max-w-[240px]">
                          <div className="min-w-0 w-full max-w-[240px] overflow-hidden">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <MapPin className="h-4 w-4 stroke-[1.8] text-rose-500 shrink-0" />
                              <span
                                className="block truncate font-medium text-slate-700 w-full"
                                title={incidente.location || '-'}
                              >
                                {incidente.location || '-'}
                              </span>
                            </div>
                          </div>
                        </Td>
                        <Td className="max-w-[140px]">
                          <div className="min-w-0 w-full max-w-[140px] overflow-hidden">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <Calendar className="h-4 w-4 stroke-[1.8] text-slate-400 shrink-0" />
                              <span
                                className="block truncate font-medium text-slate-700 tabular-nums whitespace-nowrap w-full"
                                title={formatDate(incidente.date)}
                              >
                                {formatDate(incidente.date)}
                              </span>
                            </div>
                          </div>
                        </Td>
                        <Td className="max-w-[120px]">
                          <div className="min-w-0 w-full max-w-[120px] overflow-hidden">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <Clock className="h-4 w-4 stroke-[1.8] text-slate-400 shrink-0" />
                              <span
                                className="block truncate font-medium text-slate-700 tabular-nums whitespace-nowrap w-full"
                                title={incidente.time || '-'}
                              >
                                {incidente.time || '-'}
                              </span>
                            </div>
                          </div>
                        </Td>
                        <Td className="max-w-[420px]">
                          <div className="min-w-0 w-full max-w-[420px] overflow-hidden">
                            <span
                              className="block truncate font-medium text-slate-700 w-full"
                              title={incidente.description || '-'}
                            >
                              {incidente.description || '-'}
                            </span>
                          </div>
                        </Td>
                        <Td className="max-w-[170px]">
                          <div className="min-w-0 w-full max-w-[170px] overflow-hidden">
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="flex flex-wrap gap-1 min-w-0">
                                {qtdFotos > 0 ? (
                                  incidente.images!.slice(0, 3).map((img: { url: string }, idx: number) => (
                                    <img
                                      key={idx}
                                      src={img.url}
                                      alt="Incidente"
                                      className="h-9 w-9 shrink-0 object-cover rounded-lg border border-slate-200 cursor-pointer hover:opacity-80 transition-opacity"
                                      onClick={() => handleImageClick(img.url)}
                                    />
                                  ))
                                ) : (
                                  <div className="inline-flex items-center gap-1.5 rounded-lg bg-slate-50 px-2 py-1 ring-1 ring-inset ring-slate-200">
                                    <ImageIcon className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                                    <span className="text-[11px] font-semibold text-slate-500">
                                      Sem fotos
                                    </span>
                                  </div>
                                )}
                                {qtdFotos > 3 && (
                                  <div className="inline-flex items-center justify-center h-9 min-w-[32px] rounded-lg bg-slate-900/80 px-1.5 text-[11px] font-bold text-white">
                                    +{qtdFotos - 3}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </Td>
                        <Td className="max-w-[150px]">
                          <div className="min-w-0 w-full max-w-[150px] overflow-hidden">
                            <span
                              className="block truncate font-medium text-slate-800 tabular-nums whitespace-nowrap w-full"
                              title={criado.date}
                            >
                              {criado.date}
                            </span>
                            <span
                              className="block mt-0.5 text-[12px] text-slate-500 truncate w-full tabular-nums whitespace-nowrap"
                              title={criado.time}
                            >
                              {criado.time}
                            </span>
                          </div>
                        </Td>
                        <Td className="max-w-[100px]">
                          <div className="inline-flex items-center gap-1 whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => handleEdit(incidente)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                              title="Editar incidente"
                              aria-label="Editar incidente"
                            >
                              <Edit2 className="h-[14px] w-[14px] stroke-[1.8]" />
                            </button>
                            <button
                              type="button"
                              onClick={() => abrirConfirmacaoExclusao(incidente)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-400/40 focus:ring-offset-1"
                              title="Excluir incidente"
                              aria-label="Excluir incidente"
                            >
                              <Trash2 className="h-[14px] w-[14px] stroke-[1.8]" />
                            </button>
                          </div>
                        </Td>
                      </Tr>
                    );
                  })
                )}
              </Tbody>
            </ModernTable>

            {pageRange.total > 0 ? (
              <div className="mt-5 flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-[13px] font-medium text-slate-500">
                  Mostrando{' '}
                  <span className="font-semibold text-slate-700">
                    {pageRange.start}-{pageRange.end}
                  </span>{' '}
                  de{' '}
                  <span className="font-semibold text-slate-700">{pageRange.total}</span>{' '}
                  resultados
                </div>
                <div className="inline-flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Primeira página"
                  >
                    <ChevronsLeft className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Página anterior"
                  >
                    <ChevronLeft className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <div className="inline-flex items-center gap-1 mx-1">
                    {getPageNumbers.map((n, idx) =>
                      n === '...' ? (
                        <span
                          key={`dots-${idx}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setCurrentPage(n)}
                          disabled={submitting}
                          className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1 ${
                            n === currentPage
                              ? 'bg-blue-600 text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)]'
                              : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                          }`}
                        >
                          {n}
                        </button>
                      ),
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(totalPaginas, p + 1))}
                    disabled={currentPage === totalPaginas}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Próxima página"
                  >
                    <ChevronRight className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(totalPaginas)}
                    disabled={currentPage === totalPaginas}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Última página"
                  >
                    <ChevronsRight className="h-4 w-4 stroke-[1.8]" />
                  </button>
                </div>
              </div>
            ) : null}
          </CardBody>
        </SectionCard>
      </div>

      {isModalOpen && (
        <Modal
          onClose={closeFormModal}
          maxWidthClass="max-w-2xl"
          title={
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-500 via-orange-500 to-amber-500 text-white shadow-[0_6px_16px_-8px_rgba(239,68,68,0.6)]">
                <AlertTriangle className="h-5 w-5 stroke-[1.9]" />
              </div>
              <div>
                <div className="text-[16px] font-bold tracking-tight text-slate-900 leading-none">
                  {editingIncidente ? 'Editar incidente' : 'Registrar novo incidente'}
                </div>
                <div className="mt-1 text-[12px] font-medium text-slate-500 leading-tight">
                  Preencha os campos abaixo para {editingIncidente ? 'atualizar o registro do ocorrido' : 'registrar um novo incidente na frota'}
                </div>
              </div>
            </div>
          }
          footer={
            <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:items-center">
              <button
                type="button"
                onClick={closeFormModal}
                className="inline-flex h-10 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 text-[13px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/30 disabled:pointer-events-none disabled:opacity-60"
                disabled={submitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                form="incidente-form"
                disabled={submitting}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-rose-600 via-orange-600 to-amber-600 px-6 text-[13px] font-semibold text-white shadow-[0_4px_14px_-4px_rgba(239,68,68,0.55)] transition-all hover:shadow-[0_8px_22px_-6px_rgba(239,68,68,0.65)] focus:outline-none focus:ring-4 focus:ring-orange-500/25 disabled:pointer-events-none disabled:opacity-60 disabled:shadow-none"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin stroke-[2]" />
                    Salvando...
                  </>
                ) : editingIncidente ? (
                  'Salvar alterações'
                ) : (
                  'Registrar'
                )}
              </button>
            </div>
          }
        >
          <form id="incidente-form" onSubmit={handleSubmit} className="space-y-5" noValidate>
            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-rose-500 to-orange-500" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-rose-700">
                  Dados do ocorrido
                </h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4 sm:p-5 space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <FieldWrapper
                    label={
                      <span className="inline-flex items-center gap-1">
                        <Car className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Veículo
                      </span>
                    }
                    htmlFor="incidente-vehicle"
                    required
                    error={formErrors.vehicle_id}
                  >
                    <Select
                      id="incidente-vehicle"
                      name="vehicle_id"
                      value={formData.vehicle_id || ''}
                      onChange={handleInputChange}
                    >
                      <option value="">Selecione um veículo</option>
                      {vehicles.map(vehicle => (
                        <option key={vehicle.id} value={vehicle.id}>
                          {vehicle.brand || '(Sem marca)'}
                          {vehicle.license_plate ? ` — ${vehicle.license_plate}` : ''}
                          {vehicle.description ? ` (${vehicle.description})` : ''}
                        </option>
                      ))}
                    </Select>
                  </FieldWrapper>

                  <FieldWrapper
                    label={
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Local do incidente
                      </span>
                    }
                    htmlFor="incidente-location"
                    required
                    error={formErrors.location}
                  >
                    <div className="relative">
                      <Input
                        id="incidente-location"
                        name="location"
                        value={formData.location || ''}
                        onChange={handleInputChange}
                        placeholder="Endereço, referência, região..."
                        className="pr-20"
                        maxLength={MAX_LOCAL + 50}
                      />
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <CharCounter value={formData.location || ''} max={MAX_LOCAL} />
                      </div>
                    </div>
                  </FieldWrapper>

                  <FieldWrapper
                    label={
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Data
                      </span>
                    }
                    htmlFor="incidente-date"
                    required
                    error={formErrors.date}
                  >
                    <Input
                      id="incidente-date"
                      name="date"
                      type="date"
                      value={formData.date || ''}
                      onChange={handleInputChange}
                    />
                  </FieldWrapper>

                  <FieldWrapper
                    label={
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Hora
                      </span>
                    }
                    htmlFor="incidente-time"
                    required
                    error={formErrors.time}
                  >
                    <Input
                      id="incidente-time"
                      name="time"
                      type="time"
                      value={formData.time || ''}
                      onChange={handleInputChange}
                    />
                  </FieldWrapper>
                </div>

                <div>
                  <div className="mb-1.5 flex items-end justify-between gap-2">
                    <Label htmlFor="incidente-desc" className="mb-0">
                      <span className="inline-flex items-center gap-1">
                        <FileText className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Descrição do ocorrido
                        <span className="text-rose-500 ml-0.5 font-bold leading-none">*</span>
                      </span>
                    </Label>
                    <CharCounter value={formData.description || ''} max={MAX_DESCRICAO} />
                  </div>
                  <Textarea
                    id="incidente-desc"
                    name="description"
                    value={formData.description || ''}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder="Descreva com detalhes o que aconteceu..."
                    className={`resize-y ${formErrors.description ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-500/20' : ''}`}
                    maxLength={MAX_DESCRICAO + 50}
                  />
                  {formErrors.description ? (
                    <p className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-semibold leading-tight text-rose-600">
                      <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                      {formErrors.description}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-blue-500 to-indigo-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-blue-700">
                  Envolvidos e evidências
                </h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4">
                <FieldWrapper
                  label={
                    <span className="inline-flex items-center gap-1">
                      <Users className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Pessoas envolvidas
                    </span>
                  }
                  htmlFor="incidente-involved"
                  required
                  error={formErrors.involved_employees}
                  hint="Segure Ctrl/Cmd para selecionar mais de um"
                >
                  <Select
                    id="incidente-involved"
                    name="involved_employees"
                    multiple
                    value={(formData.involved_employees || []).map(String)}
                    onChange={handleInputChange}
                    size={4}
                    className="min-h-[8.5rem]"
                  >
                    {employees.length === 0 ? (
                      <option value="" disabled>Nenhum funcionário disponível</option>
                    ) : (
                      employees.map(emp => (
                        <option key={emp.id} value={emp.id}>
                          {emp.name}
                          {emp.cargo ? ` — ${emp.cargo}` : ''}
                          {emp.cpf ? ` (${emp.cpf})` : ''}
                        </option>
                      ))
                    )}
                  </Select>
                </FieldWrapper>

                <div>
                  <div className="mb-1.5 flex items-start justify-between gap-2">
                    <div>
                      <Label className="mb-0.5">
                        <span className="inline-flex items-center gap-1">
                          <Upload className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Fotos do incidente
                        </span>
                      </Label>
                      <p className="mt-0.5 text-[11px] font-medium leading-tight text-slate-500">
                        JPG, PNG até 5MB cada · até 10 fotos
                      </p>
                    </div>
                    {photoPreviews.length > 0 && (
                      <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-0.5 ring-1 ring-inset ring-blue-200 text-[11px] font-bold text-blue-700">
                        <ImageIcon className="h-3 w-3" /> {photoPreviews.length} foto(s)
                      </span>
                    )}
                  </div>
                  <label className="group flex min-h-[110px] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50/40 px-4 py-6 text-center transition-all hover:border-blue-400 hover:bg-blue-50/40 focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-500/10">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-slate-400 ring-1 ring-slate-200 shadow-sm transition-all group-hover:text-blue-600 group-hover:ring-blue-200">
                      <Upload className="h-5 w-5 stroke-[1.9]" />
                    </div>
                    <div className="text-[13px] font-semibold text-slate-700 group-hover:text-blue-700">
                      Clique para anexar fotos
                    </div>
                    <div className="text-[12px] font-medium text-slate-500">ou arraste e solte os arquivos</div>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoChange}
                      className="sr-only"
                    />
                  </label>

                  {photoPreviews.length > 0 ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {photoPreviews.map((src, idx) => (
                        <div key={idx} className="relative group/img overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                          <img
                            src={src}
                            alt={`Preview ${idx + 1}`}
                            className="h-20 w-20 object-cover transition-transform group-hover/img:scale-[1.03]"
                          />
                          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/55 to-transparent opacity-0 transition-opacity group-hover/img:opacity-100" />
                          <button
                            type="button"
                            onClick={() => handleRemovePhoto(idx)}
                            className="absolute top-1 right-1 inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-full bg-black/75 text-white opacity-0 shadow-lg transition-all hover:bg-rose-600 group-hover/img:opacity-100 focus:outline-none focus:ring-2 focus:ring-rose-400/40 focus:ring-offset-1"
                            title="Remover foto"
                            aria-label={`Remover foto ${idx + 1}`}
                          >
                            <X className="h-3.5 w-3.5 stroke-[2.2]" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>

                {editingIncidente && editingIncidente.images && Array.isArray(editingIncidente.images) && editingIncidente.images.length > 0 ? (
                  <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-3.5">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <p className="text-[12px] font-bold text-slate-700 inline-flex items-center gap-1.5">
                        <ImageIcon className="h-3.5 w-3.5 text-slate-500" /> Fotos já anexadas no registro
                      </p>
                      <span className="text-[11px] font-semibold text-slate-500">
                        {editingIncidente.images.length} foto(s)
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {editingIncidente.images.map((img, idx) => (
                        <img
                          key={idx}
                          src={img.url}
                          alt={`Evidência ${idx + 1}`}
                          className="h-16 w-16 shrink-0 cursor-pointer object-cover rounded-lg border border-slate-200 shadow-sm hover:opacity-90 transition-opacity"
                          onClick={() => handleImageClick(img.url)}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}

                {editingIncidente && editingIncidente.involved_employees && editingIncidente.involved_employees.length > 0 ? (
                  <div className="rounded-xl border border-slate-200 bg-blue-50/40 p-3.5">
                    <div className="mb-2 flex items-center gap-1.5">
                      <Info className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                      <p className="text-[12px] font-bold text-blue-900">
                        Envolvidos já registrados no incidente
                      </p>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {renderInvolvedEmployees(editingIncidente.involved_employees)}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </form>
        </Modal>
      )}

      {selectedImage && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-8 sm:px-6"
          onClick={handleCloseImageModal}
        >
          <div
            className="absolute inset-0 bg-slate-950/75 backdrop-blur-sm"
            aria-hidden="true"
          />
          <div
            className="relative z-10 overflow-hidden rounded-2xl border border-white/10 bg-slate-900 shadow-[0_30px_80px_-20px_rgba(15,23,42,0.75)]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={handleCloseImageModal}
              className="absolute top-3 right-3 z-20 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900/80 text-white ring-1 ring-inset ring-white/10 backdrop-blur transition-colors hover:bg-slate-800 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-400/50"
              title="Fechar"
              aria-label="Fechar visualização"
            >
              <X className="h-5 w-5 stroke-[2]" />
            </button>
            <div className="bg-[radial-gradient(ellipse_at_center,rgba(30,41,59,0.55),transparent_60%)] p-4 sm:p-6">
              <img
                src={selectedImage}
                alt="Visualização ampliada"
                className="max-h-[80vh] max-w-[min(90vw,1000px)] rounded-xl object-contain shadow-[0_12px_40px_-12px_rgba(0,0,0,0.65)]"
              />
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={isDeleteConfirmOpen}
        onConfirm={confirmarExclusao}
        onCancel={() => {
          setIsDeleteConfirmOpen(false);
          setDeletingIncidente(null);
        }}
        title="Excluir incidente"
        description={`Tem certeza que deseja excluir o incidente #${deletingIncidente?.id ?? ''}? Esta ação não pode ser desfeita.`}
        confirmLabel="Sim, excluir"
        cancelLabel="Cancelar"
        confirmVariant="danger"
        loading={submitting}
      />
    </>
  );
};

export default IncidentesPage;
